import type { NotificationProgramFamily } from './inbox';

export interface NotificationInboxContext {
  programContextRevision: string;
  programFamily: NotificationProgramFamily;
}

export interface NotificationInboxRefreshTicket extends NotificationInboxContext {
  generation: number;
}

export interface NotificationInboxSnapshot<Item> extends NotificationInboxContext {
  items: Item[];
}

function sameContext(left: NotificationInboxContext, right: NotificationInboxContext): boolean {
  return (
    left.programContextRevision === right.programContextRevision &&
    left.programFamily === right.programFamily
  );
}

/**
 * Invalidates every in-flight inbox request when the trusted programme context
 * changes, and permits only the newest request for the current context to
 * commit. The guard is transport-agnostic so delayed reciprocal responses can
 * be proved without a browser or database.
 */
export function createNotificationInboxRefreshGuard(initialContext: NotificationInboxContext) {
  let currentContext = { ...initialContext };
  let generation = 0;

  return {
    syncContext(nextContext: NotificationInboxContext) {
      if (sameContext(currentContext, nextContext)) return;
      currentContext = { ...nextContext };
      generation += 1;
    },
    beginRefresh(): NotificationInboxRefreshTicket {
      generation += 1;
      return { ...currentContext, generation };
    },
    canCommit(ticket: NotificationInboxRefreshTicket): boolean {
      return generation === ticket.generation && sameContext(currentContext, ticket);
    },
  };
}

/** Hides an old-context snapshot synchronously, before effects run. */
export function visibleNotificationInboxItems<Item>(
  snapshot: NotificationInboxSnapshot<Item>,
  currentContext: NotificationInboxContext,
): Item[] {
  return sameContext(snapshot, currentContext) ? snapshot.items : [];
}
